<template>
  <BaseModal v-if="consent" :id="componentId" :show="show" size="lg" :hide-backdrop="true">
    <template #modal-header>
      <b-col class="px-0">
        <img src="@/assets/4life-logo.svg" class="my-4 img__logo-header" />
        <h5 class="display-4 mt-1 my-4 font-weight-bold">
          {{ consent.name || '' }}
        </h5>
        <div class="font-weight-bold my-4">
          Please read the following
          {{ (consent.name || '').toLowerCase() }} carefully.
        </div>
        <div class="font-weight-bold mb-3">
          Updated :
          {{
            consent.updated_at
              ? formatedDatetime(consent.updated_at, 'MMM DD, YYYY')
              : ''
          }}
        </div>
      </b-col>
    </template>

    <div v-safe-html="consent.description" class="p-no-mb"></div>

    <template #modal-footer class="modal-footer">
      <b-col class="px-0">
        <b-form name="form-consent" @submit="($event) => handleContinue(consent, $event)">
          <div v-for="(document, index) in documents" :key="document.id">
            <b-form-checkbox
              v-show="index === consentIndex"
              v-model="checked"
              name="consent"
              :value="document.id"
              class="font-weight-bold"
            >
              I have read and agree to the {{ document.name || '' }}
            </b-form-checkbox>
          </div>

          <div class="my-4">
            <b-button
              v-show="!isLastConsent"
              variant="success"
              class="mr-2"
              :disabled="!disabled"
              name="continue-button"
              @click="($event) => handleContinue(consent, $event)"
            >
              Continue
            </b-button>

            <b-button
              v-show="isLastConsent"
              variant="success"
              class="mr-2"
              :disabled="!disabled"
              type="submit"
              name="continue-submit-button"
            >
              <span v-if="!isLoading">Continue submit</span>
              <span v-else class="pr-2 pl-2">
                <b-spinner label="Loading..." variant="secondary" small />
              </span>
            </b-button>

            <b-button v-if="logoutable" variant="link" @click="onLogOut">Logout</b-button>

            <b-button v-if="cancelable" variant="link" @click="handleCancel(consent)">
              Cancel
            </b-button>
          </div>
        </b-form>
      </b-col>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/base-components/BaseModal';
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import { CatalogConsent } from '@/models/catalog/CatalogConsent';
import { useAuth } from '../../../composables';

const { logout } = useAuth();

export default {
  name: 'ModalPolicy',

  components: {
    BaseModal
  },

  mixins: [datetimeMixin, userInfoMixin, amplitudeMixin],

  props: {
    show: {
      type: Boolean,
      default: false
    },

    logoutable: {
      type: Boolean,
      default: false
    },
    cancelable: {
      type: Boolean,
      default: false
    },
    forceRequireAll: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      componentId: 'ModalPolicy',
      checked: [],
      documents: [],
      consentIndex: 0,
      isLoading: false
    };
  },

  computed: {
    consent() {
      const { consentIndex, documents } = this;

      return documents[consentIndex] || null;
    },

    disabled() {
      return this.checked.some((element) => element == this.consent.id);
    },
    isLastConsent() {
      return this.consentIndex + 1 === this.documents.length;
    }
  },

  watch: {
    show: {
      immediate: true,
      handler: 'onShowChanged'
    }
  },

  methods: {
    async fetchDocuments() {
      // For sign up flow or when forced, we just grab all active consents
      // to display directly without needing checking against user's status since they don't have one
      if (this.forceRequireAll) {
          const response = await CatalogConsent.api().fetchActive();
          const docs = response?.response?.data?.data || [];
          if (docs && docs.length) {
            this.documents = docs;
          }
          return;
      }

    // Normal flow: Check user's missing consents (via the requestConsents API response)
      try {
        const response = await CatalogConsent.api().requestConsents();
        const data = response?.response?.data?.data;
        
        if (data?.consents) {
          if (data.consents.length > 0) {
            this.documents = data.consents;
          }
        } 
      } catch (error) {
        console.error('Failed to resolve missing consents:', error);
      }
    },

    handleContinue(consent, event) {
      event.preventDefault();
      const { consentIndex, checked, documents } = this;
      let checkedId = documents
        .filter((doc) => checked.includes(doc.id))
        .map((doc) => {
          return doc.id;
        });
      const isCheckedCurrentConsent = checkedId.some((element) => element == consent.id);
      let isCheckedAllConsent = false;

      documents.forEach((document) => {
        isCheckedAllConsent = checkedId.some((element) => element == document.id);
      });

      if (consentIndex + 1 === documents.length && isCheckedAllConsent) {
        this.$emit('completed', checkedId);
        return;
      }

      if (isCheckedCurrentConsent) {
        this.consentIndex++;
        document.getElementById(`${this.componentId}___BV_modal_body_`).scrollTop = 0;
      }
    },

    handleCancel(consent) {
      const { consentIndex, checked } = this;
      for (let i = 0; i < checked.length; i++) {
        if (checked[i] === consent.id) {
          this.checked.splice(i, 1);
        }
      }

      if (consentIndex === 0) {
        this.$emit('canceled');
      } else {
        --this.consentIndex;
      }
    },

    onLogOut() {
      logout();
    },

    async onShowChanged(show) {
      if (show) {
        await this.fetchDocuments();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .modal-footer {
  justify-content: flex-start;
}

::v-deep .img__logo-header {
  height: auto;
  max-width: 335px;
}

::v-deep .modal-content {
  height: 90vh !important;
  border: none;
  box-shadow: 0rem 5rem 14rem 0 rgba(255, 255, 255, 0.3), 0 0.8rem 2.3rem rgba(0, 0, 0, 0.2),
    0 0.2rem 0.3rem rgba(0, 0, 0, 0.2);

  .modal-body {
    overflow-y: auto;
  }
}
</style>
